import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  RefreshCw, Search, Send, X, ChevronDown, ChevronRight,
  AlertTriangle, Clock, CheckCircle2, ShieldAlert,
  TrendingUp, Euro, Calendar, Mail, Plus, Tag, Loader2,
  FileText, History, Zap, User,
  ArrowUp, ArrowDown, ArrowUpDown, StickyNote, PauseCircle, PlayCircle, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getRelancesSnapshot, getRelancesDossiers, getRelanceDossierDetail,
  bulkSendRelances, forcerRelance, ajouterDestRelance, triggerRelanceSync,
  getRelancesCampaigns, patchDossierNotes, gelerFacture, reintegrerFacture,
} from '../api';
import { SidePanel } from '../components/SidePanel';
import TiersTagsFilter from '../components/TiersTagsFilter';
import DepartementFilter from '../components/DepartementFilter';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n) =>
  n == null ? '—' : Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtRelative = (iso) => {
  if (!iso) return null;
  const diff = (Date.now() - new Date(iso)) / 86400000;
  if (diff < 1) return "Aujourd'hui";
  if (diff < 2) return 'Hier';
  return `Il y a ${Math.floor(diff)}j`;
};

const bloqueDateColor = (iso) => {
  if (!iso) return 'var(--text-muted)';
  const days = (Date.now() - new Date(iso)) / 86400000;
  if (days > 30) return '#DC2626';
  if (days > 7)  return '#D97706';
  return 'var(--text-muted)';
};

const STATUT_META = {
  ELIGIBLE:    { label: 'Éligible',  color: '#059669', bg: '#ECFDF5', border: '#6EE7B7', icon: CheckCircle2 },
  IN_COOLDOWN: { label: 'Cooldown',  color: '#2563EB', bg: '#EFF6FF', border: '#93C5FD', icon: Clock },
  BLOCKED:     { label: 'Bloqué',    color: '#DC2626', bg: '#FEF2F2', border: '#FCA5A5', icon: AlertTriangle },
  PAID:        { label: 'Soldé',     color: '#6B7280', bg: '#F9FAFB', border: '#D1D5DB', icon: CheckCircle2 },
};

const BLOCAGE_LABELS = {
  NO_DEST_TAG:                     'Pas de contact',
  NO_DEBT:                         'Aucune créance',
  ACCOUNTING_CREDIT_NON_LETTRE:    'Au crédit non lettré',
  ACCOUNTING_CREDIT_BALANCE:       'Solde créditeur',
  ECHEANCE_NOT_REACHED:            'Échéance non atteinte',
  GEL_MANUEL:                      'Gel manuel',
};

const MOTIFS_GEL = [
  { value: 'DEJA_PAYE',         label: 'Déjà payé' },
  { value: 'REPORT_ECHEANCE',   label: "Report / Date d'échéance incorrecte" },
  { value: 'LITIGE',            label: 'Litige commercial' },
  { value: 'ATTENTE_AVOIR',     label: "Attente d'avoir" },
  { value: 'ACCORD_DIRECTION',  label: 'Accord direction' },
];

const GEL_MOTIF_LABELS = {
  DEJA_PAYE:        'Déjà payé',
  REPORT_ECHEANCE:  "Report / Date d'échéance incorrecte",
  LITIGE:           'Litige commercial',
  ATTENTE_AVOIR:    "Attente d'avoir / Facture rectificative",
  ACCORD_DIRECTION: 'Accord Direction / Échéancier DAF',
};

const ALL_GEL_MOTIFS = [
  { value: 'DEJA_PAYE',        label: GEL_MOTIF_LABELS.DEJA_PAYE },
  { value: 'REPORT_ECHEANCE',  label: GEL_MOTIF_LABELS.REPORT_ECHEANCE },
  { value: 'LITIGE',           label: GEL_MOTIF_LABELS.LITIGE },
  { value: 'ATTENTE_AVOIR',    label: GEL_MOTIF_LABELS.ATTENTE_AVOIR },
  { value: 'ACCORD_DIRECTION', label: GEL_MOTIF_LABELS.ACCORD_DIRECTION },
];

// Colonnes du tableau : checkbox | Tiers | Relance | Encours | Retard | Statut | Dernière relance | Notes
// Largeurs FIXES (demande utilisateur) — troncature + tooltip sur Tiers/Relance/Notes.
const GRID_COLS = '32px 520px 180px 200px 150px 200px 170px 220px';

function StatutBadge({ statut, reason, small }) {
  const meta = STATUT_META[statut] || STATUT_META.BLOCKED;
  const Icon = meta.icon;
  const label = reason ? (BLOCAGE_LABELS[reason] || reason) : meta.label;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border font-semibold ${small ? 'text-xs px-2 py-0.5' : 'text-xs px-2.5 py-1'}`}
          style={{ background: meta.bg, borderColor: meta.border, color: meta.color }}>
      <Icon size={small ? 10 : 11} />
      {label}
    </span>
  );
}

function SortableHeader({ label, col, sortBy, sortDir, onSort, align = 'left', title }) {
  const active = sortBy === col;
  const justify = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
  return (
    <button type="button" onClick={() => onSort(col)} title={title || `Trier par ${label}`}
            data-testid={`sort-${col}`}
            className={`flex items-center gap-1 min-w-0 ${justify} transition-opacity hover:opacity-100`}
            style={{ opacity: active ? 1 : 0.75, color: 'inherit', font: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}>
      <span className="truncate">{label}</span>
      {active
        ? (sortDir === 'asc' ? <ArrowUp size={11} className="shrink-0" /> : <ArrowDown size={11} className="shrink-0" />)
        : <ArrowUpDown size={10} className="shrink-0" style={{ opacity: 0.35 }} />}
    </button>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function RapportsRelances() {
  const [snapshot,   setSnapshot]   = useState(null);
  const [dossiers,   setDossiers]   = useState([]);
  const [total,      setTotal]      = useState(0);
  const [campaigns,  setCampaigns]  = useState([]);
  const [activeTab,  setActiveTab]  = useState('all');
  const [motifFlt,   setMotifFlt]   = useState('');
  const [search,     setSearch]     = useState('');
  const [campFlt,    setCampFlt]    = useState('');
  const [sortBy,     setSortBy]     = useState('');
  const [sortDir,    setSortDir]    = useState('desc');
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [loadingSnap, setLoadingSnap] = useState(false);
  const [selected,   setSelected]   = useState(new Set());
  const [sideDos,    setSideDos]    = useState(null);
  const [sideDetail, setSideDetail] = useState(null);
  const [sideLoading, setSideLoading] = useState(false);
  const [showAddDest, setShowAddDest] = useState(false);
  const [sending,    setSending]    = useState(null);
  const [syncing,    setSyncing]    = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false); // eslint-disable-line no-unused-vars
  const [creditFlt,    setCreditFlt]    = useState('');   // '' | 'prets' | 'credit_nl'
  const [eligibleDropOpen, setEligibleDropOpen] = useState(false); // eslint-disable-line no-unused-vars
  const [gelMotifFlt,  setGelMotifFlt]  = useState('');
  const [blocagePopOpen, setBlocagePopOpen] = useState(false);
  const [eligiblePopOpen, setEligiblePopOpen] = useState(false);
  const blocagePopRef = useRef(null);
  const eligiblePopRef = useRef(null);
  // Filtre tags client (tiers_tags — Party Model) — mémorisé dans le navigateur
  const [includeTags, setIncludeTags] = useState(() => {
    try { return JSON.parse(localStorage.getItem('relances_tags_filter'))?.inc || []; } catch { return []; }
  });
  const [excludeTags, setExcludeTags] = useState(() => {
    try { return JSON.parse(localStorage.getItem('relances_tags_filter'))?.exc || []; } catch { return []; }
  });
  const handleTagsChange = (inc, exc) => {
    setIncludeTags(inc);
    setExcludeTags(exc);
    try { localStorage.setItem('relances_tags_filter', JSON.stringify({ inc, exc })); } catch { /* quota */ }
  };
  // Filtre département (préfixe code postal) — mémorisé dans le navigateur
  const [depts, setDepts] = useState(() => {
    try { return JSON.parse(localStorage.getItem('relances_depts_filter')) || []; } catch { return []; }
  });
  const handleDeptsChange = (codes) => {
    setDepts(codes);
    try { localStorage.setItem('relances_depts_filter', JSON.stringify(codes)); } catch { /* quota */ }
  };
  const searchRef = useRef();
  const LIMIT = 50;

  // ── Chargement snapshot ───────────────────────────────────────────────────
  const loadSnapshot = useCallback(async () => {
    setLoadingSnap(true);
    try {
      const params = campFlt ? { campaign_ids: campFlt } : {};
      if (includeTags.length) params.tags_include = includeTags.join(',');
      if (excludeTags.length) params.tags_exclude = excludeTags.join(',');
      if (depts.length)       params.depts = depts.join(',');
      const { data } = await getRelancesSnapshot(params);
      setSnapshot(data);
    } catch {
      toast.error('Impossible de charger le snapshot');
    } finally {
      setLoadingSnap(false);
    }
  }, [campFlt, includeTags, excludeTags, depts]);

  // ── Chargement liste dossiers ─────────────────────────────────────────────
  const loadDossiers = useCallback(async (pg = 1) => {
    setLoading(true);
    const params = { page: pg, limit: LIMIT };
    if (activeTab !== 'all') params.statut = activeTab;
    if (search.trim())       params.q      = search.trim();
    if (campFlt)             params.campaign_ids = campFlt;
    if (motifFlt)            params.blocage_reason = motifFlt;
    if (creditFlt)           params.filtre_credit  = creditFlt;
    if (gelMotifFlt)         params.gel_motif      = gelMotifFlt;
    if (includeTags.length)  params.tags_include = includeTags.join(',');
    if (excludeTags.length)  params.tags_exclude = excludeTags.join(',');
    if (depts.length)        params.depts = depts.join(',');
    if (sortBy)              { params.sort_by = sortBy; params.sort_dir = sortDir; }
    try {
      const { data } = await getRelancesDossiers(params);
      setDossiers(data.items || []);
      setTotal(data.total || 0);
      setPage(pg);
    } catch {
      toast.error('Impossible de charger les dossiers');
    } finally {
      setLoading(false);
    }
  }, [activeTab, search, campFlt, motifFlt, creditFlt, gelMotifFlt, includeTags, excludeTags, depts, sortBy, sortDir]);

  useEffect(() => { loadSnapshot(); }, [loadSnapshot]);
  useEffect(() => { loadDossiers(1); setSelected(new Set()); }, [activeTab, campFlt, motifFlt, creditFlt, gelMotifFlt, includeTags, excludeTags, depts, sortBy, sortDir]);

  useEffect(() => {
    getRelancesCampaigns().then(({ data }) => setCampaigns(data || [])).catch(() => {});
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { loadDossiers(1); setSelected(new Set()); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // Fermer les popovers au clic extérieur
  useEffect(() => {
    if (!blocagePopOpen && !eligiblePopOpen) return;
    const handler = (e) => {
      if (blocagePopRef.current && !blocagePopRef.current.contains(e.target)) setBlocagePopOpen(false);
      if (eligiblePopRef.current && !eligiblePopRef.current.contains(e.target)) setEligiblePopOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [blocagePopOpen, eligiblePopOpen]);

  // ── Tri ───────────────────────────────────────────────────────────────────
  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir(col === 'tiers' || col === 'relance' ? 'asc' : 'desc');
    }
  };

  // ── Side-panel ────────────────────────────────────────────────────────────
  const openSidePanel = async (dos) => {
    setSideDos(dos);
    setSideDetail(null);
    setSideLoading(true);
    try {
      const { data } = await getRelanceDossierDetail(dos.dossier_id);
      setSideDetail(data);
    } catch {
      toast.error('Impossible de charger le détail');
    } finally {
      setSideLoading(false);
    }
  };

  const closeSidePanel = () => {
    setSideDos(null);
    setSideDetail(null);
    setShowAddDest(false);
  };

  // ── Sync manuel ──────────────────────────────────────────────────────────
  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await triggerRelanceSync();
      toast.success(`Sync terminé — ${data.nb_updated} dossier(s) mis à jour`);
      await loadSnapshot();
      await loadDossiers(1);
    } catch {
      toast.error('Erreur lors du sync');
    } finally {
      setSyncing(false);
    }
  };

  // ── Bulk send ─────────────────────────────────────────────────────────────
  const handleBulkSend = async () => {
    if (!selected.size) return;
    setSending('bulk');
    try {
      const { data } = await bulkSendRelances({ dossier_ids: Array.from(selected) });
      toast.success(`${data.nb_ok} relance(s) envoyée(s)${data.nb_err ? ` — ${data.nb_err} erreur(s)` : ''}`);
      setSelected(new Set());
      await loadDossiers(page);
      await loadSnapshot();
    } catch {
      toast.error('Erreur lors de l\'envoi groupé');
    } finally {
      setSending(null);
    }
  };

  // ── Forcer relance ────────────────────────────────────────────────────────
  const handleForcer = async (dosId) => {
    setSending(dosId);
    try {
      const { data } = await forcerRelance(dosId);
      toast.success(`${data.nb_ok} email(s) envoyé(s)`);
      if (sideDos?.dossier_id === dosId) {
        await openSidePanel(sideDos);
      }
      await loadDossiers(page);
      await loadSnapshot();
    } catch {
      toast.error('Erreur lors de l\'envoi forcé');
    } finally {
      setSending(null);
    }
  };

  // ── Filtre par motif depuis le breakdown ──────────────────────────────────
  const handleMotifFilter = (motif) => {
    setActiveTab('BLOCKED');
    setMotifFlt(motif === motifFlt ? '' : motif);
    setGelMotifFlt('');
    setBlocagePopOpen(false);
  };

  const handleGelMotifFilter = (motif) => {
    setGelMotifFlt(motif === gelMotifFlt ? '' : motif);
    setMotifFlt('');
    setActiveTab('all');
    setBlocagePopOpen(false);
  };

  // ── Sélection ─────────────────────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    const eligibleIds = dossiers.filter(d => d.statut === 'ELIGIBLE').map(d => d.dossier_id);
    const allSelected = eligibleIds.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) eligibleIds.forEach(id => next.delete(id));
      else eligibleIds.forEach(id => next.add(id));
      return next;
    });
  };

  const TABS = [
    { key: 'all',         label: 'Tous' },
    { key: 'ENCOURS',     label: 'Encours échu' },
    { key: 'ELIGIBLE',    label: 'À relancer' },
    { key: 'IN_COOLDOWN', label: 'Cooldown' },
    { key: 'BLOCKED',     label: 'Bloqués' },
    { key: 'PAID',        label: 'Soldés' },
  ];

  const selectedEligible = dossiers.filter(d => d.statut === 'ELIGIBLE' && selected.has(d.dossier_id));
  const bulkTotal = selectedEligible.reduce((s, d) => s + (d.encours_net || 0), 0);

  return (
    <div className="flex flex-col h-full" style={{ background: 'var(--bg)' }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-6 py-4 border-b"
           style={{ borderColor: 'var(--border)' }}>
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--text)' }}>
            Cockpit Recouvrement
          </h1>
          {snapshot?.source === 'relance_dossiers' && (
            <span className="text-xs px-1.5 py-0.5 rounded font-semibold"
                  style={{ background: '#ECFDF5', color: '#059669' }}>
              Moteur V2
            </span>
          )}
        </div>
        <button onClick={handleSync} disabled={syncing}
                data-testid="relance-sync-btn"
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50 transition-all"
                style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
          <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Sync…' : 'Synchroniser'}
        </button>
      </div>

      {/* ── KPIs ───────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-5 gap-3 px-6 py-4">

        {/* Card 1 — Total Encours Échu */}
        <button
          data-testid="kpi-encours"
          onClick={() => { setMotifFlt(''); setGelMotifFlt(''); setCreditFlt(''); setActiveTab(activeTab === 'ENCOURS' ? 'all' : 'ENCOURS'); }}
          className="rounded-xl border p-4 text-left transition-all hover:shadow-md"
          style={{
            background: activeTab === 'ENCOURS' ? '#FEF2F2' : 'var(--bg-secondary)',
            borderColor: activeTab === 'ENCOURS' ? '#FCA5A5' : 'var(--border)',
          }}>
          <div className="flex items-center gap-1.5 mb-1">
            <TrendingUp size={13} style={{ color: '#DC2626' }} />
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
              Total Encours Échu
            </span>
          </div>
          <div className="text-2xl font-bold" style={{ color: '#DC2626' }}>
            {loadingSnap ? '…' : fmt(snapshot?.encours_montant)}
          </div>
          <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
            {loadingSnap ? '' : `${snapshot?.encours_count ?? 0} Tiers au total`}
          </div>
        </button>

        {/* Card 2 — À Relancer + Popover sous-filtres */}
        <div ref={eligiblePopRef} className="relative rounded-xl border"
             style={{
               background: (activeTab === 'ELIGIBLE') ? '#ECFDF5' : 'var(--bg-secondary)',
               borderColor: (activeTab === 'ELIGIBLE') ? '#6EE7B7' : 'var(--border)',
             }}>
          <div
            className="p-4 cursor-pointer select-none"
            data-testid="kpi-eligible"
            onClick={() => { setCreditFlt(''); setGelMotifFlt(''); setActiveTab(activeTab === 'ELIGIBLE' ? 'all' : 'ELIGIBLE'); }}
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1.5">
                <Send size={13} style={{ color: '#059669' }} />
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                  À Relancer (Éligibles)
                </span>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setEligiblePopOpen(o => !o); setBlocagePopOpen(false); }}
                data-testid="eligible-dropdown-toggle"
                className="p-0.5 rounded transition-all"
                style={{ color: 'var(--text-muted)' }}>
                <ChevronDown size={13} style={{ transform: eligiblePopOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
            </div>
            <div className="text-2xl font-bold" style={{ color: '#059669' }}>
              {loadingSnap ? '…' : fmt(snapshot?.prets_montant)}
            </div>
            <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
              {creditFlt ? (creditFlt === 'credit_nl' ? 'Filtré : Crédit non lettré' : 'Filtré : Prêts à partir') : (loadingSnap ? '' : `${snapshot?.prets ?? 0} Tiers prêts à partir`)}
            </div>
          </div>

          {/* Popover sous-filtres éligibles */}
          {eligiblePopOpen && (
            <div className="absolute top-full left-0 mt-1 z-50 rounded-xl border shadow-xl py-2"
                 style={{ background: 'var(--bg)', borderColor: 'var(--border)', minWidth: 300 }}>
              <p className="text-xs font-semibold uppercase tracking-wide px-4 pt-1 pb-2"
                 style={{ color: 'var(--text-muted)' }}>Filtrer la liste</p>
              {[
                { key: 'prets',     label: 'Tiers prêts à partir',   count: snapshot?.prets_sans_credit, montant: snapshot?.prets_sans_credit_montant, dot: '#059669', hov: '#ECFDF5', sel: '#D1FAE5', brd: '#059669' },
                { key: 'credit_nl', label: 'Au crédit non lettré',   count: snapshot?.prets_credit_nl,   montant: snapshot?.prets_credit_nl_montant,   dot: '#D97706', hov: '#FFFBEB', sel: '#FEF3C7', brd: '#D97706' },
              ].map((f) => {
                const selected = creditFlt === f.key;
                return (
                  <button key={f.key} data-testid={`eligible-filter-${f.key}`}
                    onClick={() => { setActiveTab('ELIGIBLE'); setCreditFlt(selected ? '' : f.key); setEligiblePopOpen(false); }}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-xs transition-colors group"
                    style={{
                      background: selected ? f.sel : 'transparent',
                      color: 'var(--text)',
                      borderLeft: selected ? `3px solid ${f.brd}` : '3px solid transparent',
                    }}
                    onMouseEnter={e => { if (!selected) e.currentTarget.style.background = f.hov; }}
                    onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}>
                    <span className="flex items-center gap-2 font-medium">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: f.dot }} />
                      {f.label}
                    </span>
                    <span className="flex items-center gap-1.5 ml-3">
                      <span className="font-bold" style={{ color: f.dot }}>{fmt(f.montant)}</span>
                      <span style={{ color: 'var(--text-muted)' }}>· {f.count ?? 0}</span>
                      <ChevronRight size={11} style={{ color: selected ? f.brd : 'var(--text-muted)', opacity: 0.7 }} />
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Card 3 — Cash Bloqué + Popover filtrant */}
        <div ref={blocagePopRef} className="relative rounded-xl border"
             style={{
               background: (activeTab === 'BLOCKED' || gelMotifFlt) ? '#FFF7ED' : 'var(--bg-secondary)',
               borderColor: (activeTab === 'BLOCKED' || gelMotifFlt) ? '#FED7AA' : 'var(--border)',
             }}>
          <div
            className="p-4 cursor-pointer select-none"
            onClick={() => { setMotifFlt(''); setGelMotifFlt(''); setActiveTab(activeTab === 'BLOCKED' ? 'all' : 'BLOCKED'); }}
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1.5">
                <ShieldAlert size={13} style={{ color: '#D97706' }} />
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                  Cash Bloqué
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-bold px-1.5 py-0.5 rounded-full"
                      style={{ background: '#FEF3C7', color: '#D97706' }}>
                  {snapshot?.bloques ?? 0}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); setBlocagePopOpen(o => !o); setEligiblePopOpen(false); }}
                  data-testid="breakdown-toggle"
                  className="p-0.5 rounded transition-all"
                  style={{ color: 'var(--text-muted)' }}>
                  <ChevronDown size={13} style={{ transform: blocagePopOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                </button>
              </div>
            </div>
            <div className="text-2xl font-bold" style={{ color: '#D97706' }}>
              {loadingSnap ? '…' : fmt(snapshot?.bloques_montant)}
            </div>
            <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
              {(motifFlt || gelMotifFlt)
                ? `Filtré : ${GEL_MOTIF_LABELS[gelMotifFlt] || BLOCAGE_LABELS[motifFlt] || motifFlt || gelMotifFlt}`
                : (loadingSnap ? '' : `${snapshot?.bloques ?? 0} Tiers à corriger`)}
            </div>
          </div>

          {/* Popover filtrant */}
          {blocagePopOpen && (
            <div className="absolute top-full left-0 mt-1 z-50 rounded-xl border shadow-xl py-2"
                 style={{ background: 'var(--bg)', borderColor: 'var(--border)', minWidth: 360 }}>
              {/* Section anomalies techniques */}
              <p className="text-xs font-semibold uppercase tracking-wide px-4 pt-1 pb-1.5"
                 style={{ color: 'var(--text-muted)' }}>Anomalies techniques</p>
              {(() => {
                const m = (snapshot?.motifs_breakdown || []).find(r => r.motif === 'NO_DEST_TAG');
                const selected = motifFlt === 'NO_DEST_TAG';
                return (
                  <button data-testid="motif-NO_DEST_TAG"
                    onClick={() => handleMotifFilter('NO_DEST_TAG')}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-xs transition-colors"
                    style={{
                      background: selected ? '#FEE2E2' : 'transparent',
                      color: 'var(--text)',
                      borderLeft: selected ? '3px solid #DC2626' : '3px solid transparent',
                    }}
                    onMouseEnter={e => { if (!selected) e.currentTarget.style.background = '#FEF2F2'; }}
                    onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}>
                    <span className="flex items-center gap-2 font-medium">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: '#DC2626' }} />
                      Pas de destinataire relance (tag)
                    </span>
                    <span className="flex items-center gap-1.5 ml-3">
                      <span className="font-bold" style={{ color: '#DC2626' }}>
                        {fmt(m?.montant ?? snapshot?.no_dest_tag_montant ?? 0)}
                      </span>
                      <ChevronRight size={11} style={{ color: selected ? '#DC2626' : 'var(--text-muted)', opacity: 0.7 }} />
                    </span>
                  </button>
                );
              })()}

              {/* Séparateur */}
              <div className="my-1.5 mx-4 border-t" style={{ borderColor: 'var(--border)' }} />

              {/* Section gel manuel */}
              <p className="text-xs font-semibold uppercase tracking-wide px-4 pb-1.5"
                 style={{ color: 'var(--text-muted)' }}>Gel manuel — à la pièce</p>
              {ALL_GEL_MOTIFS.map(({ value, label }) => {
                const gelM = (snapshot?.gel_breakdown || []).find(g => g.motif_gel === value);
                const montant = gelM?.montant ?? 0;
                const selected = gelMotifFlt === value;
                return (
                  <button key={value} data-testid={`gel-motif-${value}`}
                    onClick={() => handleGelMotifFilter(value)}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-xs transition-colors"
                    style={{
                      background: selected ? '#FEF3C7' : 'transparent',
                      color: 'var(--text)',
                      borderLeft: selected ? '3px solid #D97706' : '3px solid transparent',
                    }}
                    onMouseEnter={e => { if (!selected) e.currentTarget.style.background = '#FFFBEB'; }}
                    onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}>
                    <span className="flex items-center gap-2 font-medium">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: '#D97706' }} />
                      {label}
                    </span>
                    <span className="flex items-center gap-1.5 ml-3">
                      <span className="font-bold" style={{ color: montant > 0 ? '#D97706' : 'var(--text-muted)' }}>
                        {fmt(montant)}
                      </span>
                      <ChevronRight size={11} style={{ color: selected ? '#D97706' : 'var(--text-muted)', opacity: 0.7 }} />
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Card 4 — Créances Relancées (Cooldown) */}
        <button
          data-testid="kpi-cooldown"
          onClick={() => { setMotifFlt(''); setGelMotifFlt(''); setCreditFlt(''); setActiveTab(activeTab === 'IN_COOLDOWN' ? 'all' : 'IN_COOLDOWN'); }}
          className="rounded-xl border p-4 text-left transition-all hover:shadow-md"
          style={{
            background: activeTab === 'IN_COOLDOWN' ? '#EFF6FF' : 'var(--bg-secondary)',
            borderColor: activeTab === 'IN_COOLDOWN' ? '#93C5FD' : 'var(--border)',
          }}>
          <div className="flex items-center gap-1.5 mb-1">
            <Clock size={13} style={{ color: '#2563EB' }} />
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
              Créances Relancées
            </span>
          </div>
          <div className="text-2xl font-bold" style={{ color: '#2563EB' }}>
            {loadingSnap ? '…' : fmt(snapshot?.cooldown_montant)}
          </div>
          <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
            {loadingSnap ? '' : `${snapshot?.en_cooldown ?? 0} Tiers relancés`}
          </div>
        </button>

        {/* Card 5 — Taux de Couverture */}
        <div className="rounded-xl border p-4"
             style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-1.5 mb-1">
            <CheckCircle2 size={13} style={{ color: '#2563EB' }} />
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
              Taux de Couverture
            </span>
          </div>
          <div className="text-2xl font-bold" style={{ color: '#2563EB' }}>
            {loadingSnap ? '…' : `${(snapshot?.taux_couverture ?? 0).toFixed(1)}%`}
          </div>
          <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
            (Cooldown / Total échu)
          </div>
          {/* Barre de progression */}
          <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${Math.min(snapshot?.taux_couverture ?? 0, 100)}%`,
                background: '#2563EB',
              }}
            />
          </div>
        </div>
      </div>

      {/* ── Filtres ─────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-6 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
        <div className="relative flex-1 max-w-xs">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-placeholder)' }} />
          <input
            ref={searchRef}
            data-testid="relance-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher un tiers…"
            className="w-full pl-8 pr-3 py-1.5 rounded-lg border text-sm outline-none"
            style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border)', color: 'var(--text)' }}
          />
        </div>

        {campaigns.length > 0 && (
          <select value={campFlt} onChange={e => setCampFlt(e.target.value)}
                  data-testid="relance-camp-filter"
                  className="px-3 py-1.5 rounded-lg border text-sm"
                  style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border)', color: 'var(--text)' }}>
            <option value="">Toutes les campagnes</option>
            {campaigns.map(c => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        )}

        <select value={activeTab} onChange={e => { setMotifFlt(''); setGelMotifFlt(''); setCreditFlt(''); setActiveTab(e.target.value); }}
                data-testid="relance-status-filter"
                className="px-3 py-1.5 rounded-lg border text-sm"
                style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border)', color: 'var(--text)' }}>
          {TABS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>

        {/* Filtre tags client Inclure/Exclure (tiers_tags) — s'applique au tableau ET aux KPIs */}
        <TiersTagsFilter
          includeIds={includeTags}
          excludeIds={excludeTags}
          onChange={handleTagsChange}
          testidPrefix="relances-tags"
        />

        {/* Filtre département (préfixe code postal) — tableau ET KPIs */}
        <DepartementFilter
          value={depts}
          onChange={handleDeptsChange}
          testidPrefix="relances-dept"
        />

        {/* Indicateur filtre motif actif */}
        {motifFlt && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold"
               style={{ background: '#FEF3C7', borderColor: '#FED7AA', color: '#D97706' }}>
            <ShieldAlert size={11} />
            {BLOCAGE_LABELS[motifFlt] || motifFlt}
            <button onClick={() => setMotifFlt('')} className="ml-1 hover:opacity-70">
              <X size={11} />
            </button>
          </div>
        )}

        {/* Indicateur filtre gel motif actif */}
        {gelMotifFlt && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold"
               data-testid="gel-motif-filter-chip"
               style={{ background: '#FEF9C3', borderColor: '#FDE047', color: '#A16207' }}>
            <PauseCircle size={11} />
            {GEL_MOTIF_LABELS[gelMotifFlt] || gelMotifFlt}
            <button onClick={() => setGelMotifFlt('')} className="ml-1 hover:opacity-70">
              <X size={11} />
            </button>
          </div>
        )}

        {/* Indicateur sous-filtre éligibles actif */}
        {creditFlt && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold"
               data-testid="credit-filter-chip"
               style={{ background: '#ECFDF5', borderColor: '#6EE7B7', color: '#059669' }}>
            <Send size={11} />
            {creditFlt === 'credit_nl' ? 'Au crédit non lettré' : 'Tiers prêts à partir'}
            <button onClick={() => setCreditFlt('')} className="ml-1 hover:opacity-70">
              <X size={11} />
            </button>
          </div>
        )}

        <span className="text-xs ml-auto" style={{ color: 'var(--text-muted)' }}>
          {loading ? '…' : `${total} dossier(s)`}
        </span>
      </div>

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto">

        {/* Bulk bar */}
        {selected.size > 0 && (
          <div className="sticky top-0 z-10 flex items-center gap-3 px-6 py-2.5 border-b"
               style={{ background: '#EFF6FF', borderColor: '#93C5FD' }}>
            <span className="text-sm font-semibold" style={{ color: '#1D4ED8' }}>
              {selected.size} sélectionné(s) — {selectedEligible.length} éligible(s) — {fmt(bulkTotal)}
            </span>
            <button onClick={handleBulkSend}
                    disabled={selectedEligible.length === 0 || sending === 'bulk'}
                    data-testid="bulk-send-btn"
                    className="ml-auto flex items-center gap-2 px-4 py-1.5 rounded-lg font-semibold text-sm text-white disabled:opacity-50"
                    style={{ background: '#1D4ED8' }}>
              {sending === 'bulk'
                ? <><Loader2 size={13} className="animate-spin" /> Envoi…</>
                : <><Zap size={13} /> Envoyer la sélection ({selectedEligible.length})</>}
            </button>
            <button onClick={() => setSelected(new Set())}
                    className="p-1 rounded" style={{ color: '#1D4ED8' }}>
              <X size={14} />
            </button>
          </div>
        )}

        {/* Header table */}
        <div className="grid text-xs font-semibold uppercase tracking-wide px-6 py-2 sticky top-0 z-[5] items-center"
             style={{
               gridTemplateColumns: GRID_COLS,
               background: 'var(--bg)',
               borderBottom: '1px solid var(--border)',
               color: 'var(--text-muted)',
             }}>
          <div className="flex items-center">
            {dossiers.some(d => d.statut === 'ELIGIBLE') && (
              <input type="checkbox"
                     data-testid="header-checkbox"
                     onChange={toggleAll}
                     checked={dossiers.filter(d=>d.statut==='ELIGIBLE').every(d=>selected.has(d.dossier_id))}
                     className="w-4 h-4 rounded cursor-pointer" />
            )}
          </div>
          <SortableHeader label="Tiers" col="tiers" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Relance" col="relance" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Encours échu" col="encours" align="right" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Retard max" col="retard" align="center" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}
                          title="Jours écoulés depuis la plus ancienne échéance impayée (toutes écritures non lettrées, y compris reports à nouveau)" />
          <span className="text-center">Statut</span>
          <SortableHeader label="Dernière relance" col="relance_date" align="center" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
          <span className="flex items-center gap-1">
            <StickyNote size={10} />
            Notes
          </span>
        </div>

        {/* Rows */}
        {loading && dossiers.length === 0 ? (
          <div className="flex items-center justify-center flex-1 py-20">
            <Loader2 size={24} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
          </div>
        ) : dossiers.length === 0 ? (
          <div className="flex flex-col items-center justify-center flex-1 py-20 gap-3"
               style={{ color: 'var(--text-muted)' }}>
            <CheckCircle2 size={32} />
            <p className="text-sm">Aucun dossier dans cet onglet</p>
          </div>
        ) : (
          dossiers.map(dos => (
            <DossierRow
              key={dos.dossier_id}
              dos={dos}
              isSelected={selected.has(dos.dossier_id)}
              isActive={sideDos?.dossier_id === dos.dossier_id}
              onToggle={() => toggleSelect(dos.dossier_id)}
              onClick={() => openSidePanel(dos)}
            />
          ))
        )}

        {/* Pagination */}
        {total > LIMIT && (
          <div className="flex items-center gap-2 justify-center py-4">
            <button disabled={page === 1} onClick={() => loadDossiers(page - 1)}
                    className="px-3 py-1 text-sm rounded border disabled:opacity-40"
                    style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
              ← Préc
            </button>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Page {page} / {Math.ceil(total / LIMIT)}
            </span>
            <button disabled={page * LIMIT >= total} onClick={() => loadDossiers(page + 1)}
                    className="px-3 py-1 text-sm rounded border disabled:opacity-40"
                    style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
              Suiv →
            </button>
          </div>
        )}
      </div>

      {/* ── Side-panel (tiroir standard de l'app) ───────────────────────── */}
      <SidePanel
        open={!!sideDos}
        onOpenChange={(open) => { if (!open) closeSidePanel(); }}
        title={sideDos?.tiers_nom || sideDos?.tiers_id || 'Dossier de relance'}
        copyValue={sideDos?.tiers_nom || sideDos?.tiers_id || undefined}
        subtitle={sideDos?.campagne_label || ''}
        width={780}
        headerExtra={sideDos && (
          <StatutBadge statut={sideDos.statut} reason={sideDos.blocage_reason} small />
        )}
      >
        {sideDos && (
          <div data-testid="side-panel">
            <DossierPanelBody
              dos={sideDos}
              detail={sideDetail}
              loading={sideLoading}
              sending={sending}
              onForcer={() => handleForcer(sideDos.dossier_id)}
              onAddDest={() => setShowAddDest(true)}
              onNoteSaved={(dosId, notes) => {
                // Met à jour la note dans la liste sans recharger
                setDossiers(prev => prev.map(d =>
                  d.dossier_id === dosId ? { ...d, notes } : d
                ));
                // Met aussi à jour sideDos pour la cohérence
                setSideDos(prev => prev?.dossier_id === dosId ? { ...prev, notes } : prev);
              }}
              onDetailRefresh={async () => {
                if (!sideDos) return;
                setSideLoading(true);
                try {
                  const { data } = await getRelanceDossierDetail(sideDos.dossier_id);
                  setSideDetail(data);
                } catch { /* silencieux */ } finally {
                  setSideLoading(false);
                }
              }}
            />
          </div>
        )}
        {/* Modale rendue DANS l'arbre du Sheet pour éviter le blocage inert de Radix */}
        {showAddDest && sideDos && (
          <AddDestModal
            dossier={sideDos}
            detail={sideDetail}
            onClose={() => setShowAddDest(false)}
            onSuccess={async () => {
              setShowAddDest(false);
              toast.success('Contact taggué — dossier re-synchronisé');
              await openSidePanel(sideDos);
              await loadDossiers(page);
              await loadSnapshot();
            }}
          />
        )}
      </SidePanel>
    </div>
  );
}

// ─── Ligne de tableau ─────────────────────────────────────────────────────────

function DossierRow({ dos, isSelected, isActive, onToggle, onClick }) {
  const retard = dos.oldest_overdue_maturity
    ? Math.floor((Date.now() - new Date(dos.oldest_overdue_maturity)) / 86400000)
    : null;

  const bloquedSince = dos.bloque_depuis;

  return (
    <div
      data-testid={`dossier-row-${dos.dossier_id}`}
      onClick={onClick}
      className="grid items-center px-6 py-3 border-b cursor-pointer transition-colors"
      style={{
        gridTemplateColumns: GRID_COLS,
        borderColor: 'var(--border)',
        background: isActive ? 'var(--bg-secondary)' : 'transparent',
      }}
      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--bg-hover, var(--bg-secondary))'; }}
      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
    >
      <div className="flex items-center" onClick={e => { e.stopPropagation(); onToggle(); }}>
        {dos.statut === 'ELIGIBLE' ? (
          <input type="checkbox" checked={isSelected} onChange={() => {}}
                 className="w-4 h-4 rounded cursor-pointer" />
        ) : <span />}
      </div>

      <div className="min-w-0 pr-2">
        <p className="text-sm font-semibold truncate" style={{ color: 'var(--text)' }} title={dos.tiers_nom || ''}>
          {dos.tiers_nom || dos.tiers_id}
        </p>
      </div>

      <div className="min-w-0 pr-2">
        <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }} title={dos.campagne_label || ''}>
          {dos.campagne_label || '—'}
        </p>
      </div>

      <div className="text-sm font-semibold text-right" style={{ color: dos.encours_net > 0 ? 'var(--text)' : 'var(--text-muted)' }}>
        {fmt(dos.encours_net)}
      </div>

      <div className="text-sm text-center font-semibold"
           style={{ color: retard > 60 ? '#DC2626' : retard > 30 ? '#D97706' : 'var(--text-muted)' }}>
        {retard != null ? `${retard}j` : '—'}
      </div>

      <div className="flex flex-col gap-0.5 min-w-0 items-center">
        <StatutBadge statut={dos.statut} reason={dos.blocage_reason} small />
        {dos.statut === 'BLOCKED' && bloquedSince && (
          <span className="text-xs" style={{ color: bloqueDateColor(bloquedSince) }}>
            Depuis {fmtRelative(bloquedSince)}
          </span>
        )}
        {dos.statut === 'IN_COOLDOWN' && dos.prochaine_relance_at && (
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Re-relance {fmtDate(dos.prochaine_relance_at)}
          </span>
        )}
      </div>

      <div className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
        {dos.derniere_relance_at ? fmtRelative(dos.derniere_relance_at) : '—'}
        {dos.nb_relances_effectuees > 0 && (
          <span className="block text-xs">{dos.nb_relances_effectuees}× envoyé</span>
        )}
      </div>

      <div className="min-w-0 pr-2" title={dos.notes || ''}>
        {dos.notes ? (
          <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
            {dos.notes.length > 100 ? dos.notes.slice(0, 100) + '…' : dos.notes}
          </p>
        ) : (
          <span style={{ color: 'var(--border)' }}>—</span>
        )}
      </div>
    </div>
  );
}

// ─── Contenu du side-panel ────────────────────────────────────────────────────

function DossierPanelBody({ dos, detail, loading, sending, onForcer, onAddDest, onNoteSaved, onDetailRefresh }) {
  const d = detail?.dossier || dos;
  const [tab, setTab] = useState('ecritures'); // ecritures | historique
  const retardJours = d?.retard_max_jours;
  const bloqueDays = d?.bloque_depuis
    ? Math.floor((Date.now() - new Date(d.bloque_depuis)) / 86400000)
    : null;

  // ── Notes ──────────────────────────────────────────────────────────────────
  const [noteText,   setNoteText]   = useState(d?.notes || '');
  const [noteSaving, setNoteSaving] = useState(false);
  const noteTimerRef = useRef(null);

  // Synchronise le texte si le dossier change (ouverture d'un autre side-panel)
  const dosId = d?.dossier_id;
  React.useEffect(() => {
    setNoteText(d?.notes || '');
  }, [dosId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveNote = React.useCallback(async (text) => {
    setNoteSaving(true);
    try {
      await patchDossierNotes(d.dossier_id, text);
      onNoteSaved && onNoteSaved(d.dossier_id, text.trim() || null);
    } catch {
      toast.error('Erreur lors de la sauvegarde de la note');
    } finally {
      setNoteSaving(false);
    }
  }, [d?.dossier_id, onNoteSaved]);

  const handleNoteChange = (e) => {
    const val = e.target.value;
    setNoteText(val);
    if (noteTimerRef.current) clearTimeout(noteTimerRef.current);
    noteTimerRef.current = setTimeout(() => saveNote(val), 900);
  };

  const handleNoteBlur = () => {
    if (noteTimerRef.current) { clearTimeout(noteTimerRef.current); noteTimerRef.current = null; }
    saveNote(noteText);
  };

  // ── Gel de facture ──────────────────────────────────────────────────────────
  const [gelModal,  setGelModal]  = useState(null);  // { reference, description } | null
  const [gelMotif,  setGelMotif]  = useState('DEJA_PAYE');
  const [gelComment, setGelComment] = useState('');
  const [gelDate,   setGelDate]   = useState('');   // nouvelle_date_echeance pour REPORT_ECHEANCE
  const [gelLoading, setGelLoading] = useState(false);
  const [gelNoteOpen, setGelNoteOpen] = useState(null); // { reference, motif_gel, gel_commentaire, x, y }

  const handleGeler = async () => {
    if (!gelModal) return;
    setGelLoading(true);
    try {
      await gelerFacture(d.dossier_id, {
        id_ecriture:             gelModal.reference,
        numero_piece_ref:        gelModal.description || null,
        motif_gel:               gelMotif,
        commentaire:             gelComment.trim() || null,
        nouvelle_date_echeance:  gelMotif === 'REPORT_ECHEANCE' ? gelDate || null : null,
      });
      toast.success('Écriture gelée — exclue des prochaines relances');
      setGelModal(null);
      setGelComment('');
      setGelDate('');
      onDetailRefresh && onDetailRefresh();
    } catch {
      toast.error('Erreur lors du gel');
    } finally {
      setGelLoading(false);
    }
  };

  const handleReintegrer = async (gelId) => {
    try {
      await reintegrerFacture(gelId);
      toast.success('Écriture réintégrée — sera incluse dans les prochaines relances');
      onDetailRefresh && onDetailRefresh();
    } catch {
      toast.error('Erreur lors de la réintégration');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
      </div>
    );
  }

  return (
    <>
      {/* KPIs mini */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[
          { label: 'Encours échu',  value: fmt(d?.encours_net),       icon: Euro },
          { label: 'Retard max',    value: retardJours != null ? `${retardJours}j` : '—', icon: Calendar },
          { label: 'Nb relances',   value: d?.nb_relances_effectuees ?? 0, icon: History },
        ].map((kpi) => (
          <div key={kpi.label} className="rounded-lg p-3 border text-center"
               style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border)' }}>
            <kpi.icon size={14} className="mx-auto mb-1" style={{ color: 'var(--text-muted)' }} />
            <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>{kpi.value}</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Blocage */}
      {d?.statut === 'BLOCKED' && (
        <div className="mb-3 rounded-xl border p-3"
             style={{ background: '#FEF2F2', borderColor: '#FCA5A5' }}>
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" style={{ color: '#DC2626' }} />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold" style={{ color: '#DC2626' }}>
                {BLOCAGE_LABELS[d.blocage_reason] || d.blocage_reason || 'Bloqué'}
              </p>
              {d.blocage_detail && (
                <p className="text-xs mt-0.5" style={{ color: '#991B1B' }}>{d.blocage_detail}</p>
              )}
              {bloqueDays != null && (
                <p className="text-xs mt-1 font-semibold"
                   style={{ color: bloqueDays > 30 ? '#DC2626' : bloqueDays > 7 ? '#D97706' : '#6B7280' }}>
                  Bloqué depuis {bloqueDays}j
                  {bloqueDays > 30 && ' — ⚠ Cash gelé'}
                </p>
              )}
            </div>
          </div>
          {d.blocage_reason === 'NO_DEST_TAG' && (
            <button onClick={onAddDest}
                    data-testid="add-dest-btn"
                    className="w-full mt-2 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold border transition-all"
                    style={{ background: '#FEE2E2', borderColor: '#FCA5A5', color: '#DC2626' }}>
              <Plus size={12} />
              Ajouter un destinataire de relance
            </button>
          )}
        </div>
      )}

      {/* Cooldown */}
      {d?.statut === 'IN_COOLDOWN' && (
        <div className="mb-3 rounded-xl border p-3"
             style={{ background: '#EFF6FF', borderColor: '#93C5FD' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold" style={{ color: '#1D4ED8' }}>En cooldown</p>
              {d.prochaine_relance_at && (
                <p className="text-xs mt-0.5" style={{ color: '#3B82F6' }}>
                  Re-relance prévue le {fmtDate(d.prochaine_relance_at)}
                </p>
              )}
            </div>
            <button onClick={onForcer}
                    disabled={sending === d.dossier_id}
                    data-testid="forcer-relance-btn"
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                    style={{ background: '#2563EB' }}>
              {sending === d.dossier_id
                ? <Loader2 size={11} className="animate-spin" />
                : <Zap size={11} />}
              Forcer
            </button>
          </div>
        </div>
      )}

      {/* Éligible : action directe */}
      {d?.statut === 'ELIGIBLE' && (
        <div className="mb-3 rounded-xl border p-3 flex items-center justify-between"
             style={{ background: '#ECFDF5', borderColor: '#6EE7B7' }}>
          <div>
            <p className="text-xs font-bold" style={{ color: '#059669' }}>Prêt à être relancé</p>
            <p className="text-xs mt-0.5" style={{ color: '#065F46' }}>
              {(d.destinataires || []).length} destinataire(s) taggué(s)
            </p>
          </div>
          <button onClick={onForcer}
                  disabled={sending === d.dossier_id}
                  data-testid="send-relance-btn"
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                  style={{ background: '#059669' }}>
            {sending === d.dossier_id ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            Relancer
          </button>
        </div>
      )}

      {/* Destinataires */}
      {(d?.destinataires || []).length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>
            DESTINATAIRES TAGGUÉS
          </p>
          {(d.destinataires || []).map((dest, i) => (
            <div key={i} className="flex items-center gap-2 py-1">
              <User size={12} className="shrink-0" style={{ color: 'var(--text-muted)' }} />
              <span className="text-xs truncate" style={{ color: 'var(--text)' }}>
                {dest.prenom} {dest.nom} — {dest.email}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Notes ADV */}
      <div className="mb-3">
        <div className="flex items-center gap-1.5 mb-1.5">
          <StickyNote size={12} style={{ color: 'var(--text-muted)' }} />
          <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
            Notes ADV
          </span>
          {noteSaving && (
            <Loader2 size={10} className="animate-spin ml-auto" style={{ color: 'var(--text-muted)' }} />
          )}
        </div>
        <textarea
          data-testid="dossier-notes-textarea"
          value={noteText}
          onChange={handleNoteChange}
          onBlur={handleNoteBlur}
          placeholder="Saisir une note interne (relance téléphonique, promesse de paiement, accord…)"
          rows={3}
          className="w-full rounded-lg border px-3 py-2 text-xs resize-none outline-none transition-colors"
          style={{
            background: 'var(--bg-secondary)',
            borderColor: 'var(--border)',
            color: 'var(--text)',
          }}
        />
      </div>

      {/* Onglets détail */}
      <div className="flex border-b" style={{ borderColor: 'var(--border)' }}>
        {[
          { key: 'ecritures', label: 'Écritures', icon: FileText, count: detail?.ecritures?.length },
          { key: 'historique', label: 'Historique emails', icon: Mail, count: detail?.historique?.length },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  className="flex items-center gap-1 px-3 py-2 text-xs font-medium border-b-2 transition-all"
                  style={{
                    borderBottomColor: tab === t.key ? 'var(--primary)' : 'transparent',
                    color: tab === t.key ? 'var(--primary)' : 'var(--text-muted)',
                  }}>
            <t.icon size={11} />
            {t.label}
            {t.count != null && <span className="ml-1 px-1 py-0.5 rounded text-xs" style={{ background: 'var(--bg-secondary)' }}>{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="pt-2 pb-4">
        {tab === 'ecritures' && (
          !detail?.ecritures?.length ? (
            <p className="text-xs text-center py-4" style={{ color: 'var(--text-muted)' }}>Aucune écriture non lettrée</p>
          ) : (() => {
            const ecritures = detail.ecritures;
            // Calcul du Solde Net Relançable = débits non gelés - crédits
            const debitActif  = ecritures.filter(e => !e.est_gelee && (e.debit || 0) > 0).reduce((s, e) => s + e.debit, 0);
            const creditTotal = ecritures.filter(e => (e.credit || 0) > 0).reduce((s, e) => s + e.credit, 0);
            const soldeNet    = Math.max(debitActif - creditTotal, 0);
            const hasGel      = ecritures.some(e => e.est_gelee);

            return (
              <div data-testid="ecritures-table">
                {/* En-têtes */}
                <div className="grid gap-2 pb-1.5 border-b text-xs font-semibold uppercase tracking-wide"
                     style={{ gridTemplateColumns: '1fr 75px 75px 50px 90px 80px', borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                  <span>Libellé</span>
                  <span>Date</span>
                  <span>Échéance</span>
                  <span>Retard</span>
                  <span className="text-right">Montant</span>
                  <span className="text-right">Action</span>
                </div>
                <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
                  {ecritures.map((e, i) => {
                    const isCredit = (e.credit || 0) > 0;
                    const isGelee  = e.est_gelee;
                    return (
                      <div key={i} className="grid gap-2 items-center py-1.5 text-xs"
                           style={{
                             gridTemplateColumns: '1fr 75px 75px 50px 90px 80px',
                             borderColor: 'var(--border)',
                             background: isGelee ? 'rgba(234,179,8,0.05)' : 'transparent',
                           }}>
                        <span className="min-w-0 truncate font-medium flex items-center gap-1"
                              title={e.description || e.reference}
                              style={{ color: isGelee ? '#A16207' : 'var(--text)' }}>
                          {isCredit && <span className="shrink-0 px-1 py-0.5 rounded text-[10px] font-bold" style={{ background: '#FEF3C7', color: '#D97706' }}>À lettrer</span>}
                          {isGelee && (
                            <span className="relative shrink-0">
                              <button
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  if (gelNoteOpen?.reference === e.reference) {
                                    setGelNoteOpen(null);
                                  } else {
                                    const rect = ev.currentTarget.getBoundingClientRect();
                                    setGelNoteOpen({
                                      reference: e.reference,
                                      motif_gel: e.motif_gel,
                                      gel_commentaire: e.gel_commentaire,
                                      x: rect.left,
                                      y: rect.bottom + 4,
                                    });
                                  }
                                }}
                                className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-bold transition-all hover:opacity-80"
                                style={{ background: '#FEF3C7', color: '#A16207' }}>
                                <MessageSquare size={9} />
                                Gelée · voir la note
                              </button>
                            </span>
                          )}
                          <span className="truncate">{e.description || e.reference}</span>
                        </span>
                        <span style={{ color: 'var(--text-muted)' }}>{fmtDate(e.date_document)}</span>
                        <span style={{ color: 'var(--text-muted)' }}>{fmtDate(e.date_maturity)}</span>
                        <span className="font-semibold"
                              style={{ color: (!isCredit && !isGelee && (e.retard_jours ?? 0) > 30) ? '#DC2626' : (!isCredit && !isGelee && (e.retard_jours ?? 0) > 0) ? '#D97706' : 'transparent' }}>
                          {(!isCredit && !isGelee && e.retard_jours != null && e.retard_jours > 0) ? `+${e.retard_jours}j` : '·'}
                        </span>
                        <span className="text-right font-semibold"
                              style={{ color: isCredit ? '#D97706' : isGelee ? '#A16207' : 'var(--text)' }}>
                          {isCredit ? `−${fmt(e.credit)}` : fmt(e.debit)}
                        </span>
                        <span className="text-right">
                          {!isCredit && (
                            isGelee ? (
                              <button
                                data-testid={`reintegrer-${e.reference}`}
                                onClick={() => handleReintegrer(e.gel_id)}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold text-white transition-all hover:opacity-80"
                                style={{ background: '#059669' }}>
                                <PlayCircle size={9} /> Réintégrer
                              </button>
                            ) : (
                              <button
                                data-testid={`geler-${e.reference}`}
                                onClick={() => { setGelModal(e); setGelMotif('DEJA_PAYE'); setGelComment(''); setGelDate(''); }}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold text-white transition-all hover:opacity-80"
                                style={{ background: '#D97706' }}>
                                <PauseCircle size={9} /> Geler
                              </button>
                            )
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Pied : Solde Net Relançable */}
                <div className="mt-2 pt-2 border-t flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
                  <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
                    Solde Net Relançable{hasGel && <span className="ml-1 text-[10px]" style={{ color: '#A16207' }}>(hors gelé)</span>}
                  </span>
                  <span className="text-sm font-bold" style={{ color: soldeNet > 0 ? '#DC2626' : '#059669' }}>
                    {fmt(soldeNet)}
                  </span>
                </div>
              </div>
            );
          })()
        )}
        {tab === 'historique' && (
          !detail?.historique?.length ? (
            <p className="text-xs text-center py-4" style={{ color: 'var(--text-muted)' }}>Aucun envoi enregistré</p>
          ) : (
            <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
              {detail.historique.map((h, i) => (
                <div key={i} className="flex items-center gap-2 py-1.5 text-xs whitespace-nowrap"
                     style={{ borderColor: 'var(--border)' }}
                     title={h.err_msg || h.sujet || undefined}>
                  <span className="shrink-0 w-[72px]" style={{ color: 'var(--text-muted)' }}>
                    {fmtDate(h.date)}
                  </span>
                  <span className="flex-1 min-w-0 truncate" style={{ color: 'var(--text)' }}>
                    {h.email}
                  </span>
                  <span className={`shrink-0 font-semibold ${h.statut === 'sent' || h.statut === 'mock' ? 'text-green-600' : 'text-red-600'}`}>
                    {h.statut === 'sent' ? 'Envoyé' : h.statut === 'mock' ? 'Test' : 'Erreur'}
                  </span>
                </div>
              ))}
            </div>
          )
        )}
      </div>


      {/* Popover note de gel — portal fixed pour éviter le clipping */}
      {gelNoteOpen && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setGelNoteOpen(null)} />
          <div className="fixed z-[9999] rounded-lg border shadow-xl p-3"
               style={{
                 left: Math.min(gelNoteOpen.x, window.innerWidth - 240),
                 top: gelNoteOpen.y,
                 background: 'var(--bg)',
                 borderColor: '#FDE047',
                 minWidth: 220,
                 maxWidth: 300,
               }}>
            <p className="text-[10px] font-semibold mb-1.5" style={{ color: '#A16207' }}>
              {GEL_MOTIF_LABELS[gelNoteOpen.motif_gel] || gelNoteOpen.motif_gel || 'Gel manuel'}
            </p>
            {gelNoteOpen.gel_commentaire
              ? <p className="text-xs leading-relaxed" style={{ color: 'var(--text)' }}>{gelNoteOpen.gel_commentaire}</p>
              : <p className="text-xs italic" style={{ color: 'var(--text-muted)' }}>Aucun commentaire</p>}
          </div>
        </>,
        document.body
      )}


      {/* Modale de gel */}
      {gelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="rounded-2xl border shadow-2xl w-full max-w-sm mx-4"
               data-testid="gel-modal"
               style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border)' }}>
              <h3 className="font-bold text-sm" style={{ color: 'var(--text)' }}>Geler cette écriture</h3>
              <button onClick={() => setGelModal(null)} className="p-1 rounded" style={{ color: 'var(--text-muted)' }}>
                <X size={14} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-xs truncate font-medium" style={{ color: 'var(--text)' }}>
                {gelModal.description || gelModal.reference}
                <span className="ml-2 font-bold" style={{ color: '#DC2626' }}>{fmt(gelModal.debit)}</span>
              </p>
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--text-muted)' }}>Motif</label>
                <select value={gelMotif} onChange={e => setGelMotif(e.target.value)}
                        data-testid="gel-motif-select"
                        className="w-full px-3 py-2 rounded-lg border text-sm"
                        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border)', color: 'var(--text)' }}>
                  {MOTIFS_GEL.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              {gelMotif === 'REPORT_ECHEANCE' && (
                <div>
                  <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--text-muted)' }}>
                    Nouvelle date d'échéance <span style={{ color: '#DC2626' }}>*</span>
                  </label>
                  <input
                    type="date"
                    data-testid="gel-date-input"
                    value={gelDate}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={e => setGelDate(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
                    style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border)', color: 'var(--text)' }}
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    Le gel sera levé automatiquement à la prochaine sync (04h30) à partir de cette date.
                  </p>
                </div>
              )}
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--text-muted)' }}>Commentaire (optionnel)</label>
                <input
                  type="text"
                  data-testid="gel-commentaire-input"
                  value={gelComment}
                  onChange={e => setGelComment(e.target.value)}
                  placeholder="Ex: Litige sur livraison du 12/09"
                  className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
                  style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border)', color: 'var(--text)' }}
                />
              </div>
              <button onClick={handleGeler}
                      disabled={gelLoading || (gelMotif === 'REPORT_ECHEANCE' && !gelDate)}
                      data-testid="gel-confirm-btn"
                      className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                      style={{ background: '#D97706' }}>
                {gelLoading ? <Loader2 size={13} className="animate-spin" /> : <PauseCircle size={13} />}
                Confirmer le gel
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

// ─── Modale Ajouter destinataire ──────────────────────────────────────────────

function AddDestModal({ dossier, detail, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const personnes = detail?.personnes_liees || [];

  const handleTag = async (personneId) => {
    setLoading(true);
    try {
      await ajouterDestRelance(dossier.dossier_id, { personne_id: personneId });
      onSuccess();
    } catch {
      toast.error('Erreur lors du tag');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center"
         style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="rounded-2xl border shadow-2xl w-full max-w-md mx-4 max-h-[85vh] flex flex-col"
           data-testid="add-dest-modal"
           style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'var(--border)' }}>
          <div>
            <h3 className="font-bold text-sm" style={{ color: 'var(--text)' }}>Ajouter un destinataire de relance</h3>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{dossier.tiers_nom}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg" style={{ color: 'var(--text-muted)' }}>
            <X size={16} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          <div className="space-y-2">
            {personnes.length === 0 ? (
              <p className="text-xs text-center py-4" style={{ color: 'var(--text-muted)' }}>
                Aucune personne liée à cette entreprise — créez le contact depuis l'Annuaire puis revenez le taguer ici.
              </p>
            ) : personnes.map(p => (
              <div key={p.personne_id}
                   className="flex items-center justify-between p-3 rounded-lg border"
                   style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border)' }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
                    {p.prenom} {p.nom}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{p.email || 'Pas d\'email'}</p>
                </div>
                {p.a_le_tag ? (
                  <span className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full"
                        style={{ background: '#ECFDF5', color: '#059669' }}>
                    <Tag size={10} /> Déjà taggué
                  </span>
                ) : (
                  <button onClick={() => handleTag(p.personne_id)}
                          disabled={loading || !p.email}
                          data-testid={`tag-personne-${p.personne_id}`}
                          className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-40"
                          style={{ background: 'var(--primary)', color: '#fff' }}>
                    {loading ? <Loader2 size={11} className="animate-spin" /> : <Tag size={11} />}
                    Tagger
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
